import React, { useCallback } from 'react';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { PlanCell, PlanOperation, StructuralObject } from '../../../types/plan';
import type { PlanningActionType, PlanningSelectionInfo } from '../../../types/planManagement';
import { planCellKey } from '../../../utils/planCells';
import i18n from '../../../i18n';
import {
  buildGateOps,
  buildShadeStructureOperation,
  buildStructuralObjectEraseOps,
  isGateAction,
  isShadeStructureAction,
  SHADE_STRUCTURE_PRESETS,
  trimmedFenceCellKeys,
  validateGateCells,
} from './planningStructuralObjects';

interface UsePlanningActionHandlerParams {
  planningSelection: PlanningSelectionInfo | null;
  /** Клетки и объекты плана — чтобы стирание подрезало заборы, а не сносило их целиком */
  cells?: PlanCell[];
  structuralObjects?: StructuralObject[];
  onBedSelection?: (cells: Array<{ row: number; col: number }>) => void;
  onPatch: (ops: PlanOperation[]) => Promise<void>;
  clearPlanningSelection: () => void;
}

export const usePlanningActionHandler = ({
  planningSelection,
  cells,
  structuralObjects,
  onBedSelection,
  onPatch,
  clearPlanningSelection,
}: UsePlanningActionHandlerParams) => {
  return useCallback((action: PlanningActionType) => {
    if (!planningSelection || planningSelection.totalCells === 0) return;

    const cellsFromKeys = () => Array.from(planningSelection.selectedCellKeys).map((key) => {
      const [r, c] = key.split(':').map(Number);
      return { row: r, col: c };
    });

    if (isGateAction(action)) {
      const gateCells = cellsFromKeys();
      if (validateGateCells(gateCells, SHADE_STRUCTURE_PRESETS[action].gateLeaves ?? 1) !== null) return;
      onPatch(buildGateOps({
        action,
        cells: gateCells,
        structuralObjects: structuralObjects ?? [],
        planCells: cells ?? [],
      }));
      clearPlanningSelection();
      return;
    }

    if (isShadeStructureAction(action)) {
      onPatch([buildShadeStructureOperation(action, cellsFromKeys())]);
      clearPlanningSelection();
      return;
    }

    switch (action) {
      case 'create-bed':
        onBedSelection?.(cellsFromKeys());
        clearPlanningSelection();
        break;
      case 'fill-path':
        onPatch([{ op: 'paintCells', cells: cellsFromKeys().map((c) => ({ ...c, kind: 'path' as const })) }]);
        clearPlanningSelection();
        break;
      case 'greenhouse':
        onPatch([{ op: 'createStructuralObject', cells: cellsFromKeys(), kind: 'covering', subtype: 'greenhouse' }]);
        clearPlanningSelection();
        break;
      case 'delete': {
        const bedCount = planningSelection.beds.length;
        const entityCount = planningSelection.pathCellCount + planningSelection.obstacleCellCount + bedCount;
        const eraseInput = {
          structuralObjectIds: planningSelection.structuralObjectIds,
          selectedCellKeys: planningSelection.selectedCellKeys,
          structuralObjects: structuralObjects ?? [],
          cells: cells ?? [],
        };
        const structuralDeleteOps = buildStructuralObjectEraseOps(eraseInput);
        // Под подрезанным забором дорожка остаётся — эти клетки не стираются.
        const keepCellKeys = trimmedFenceCellKeys(eraseInput);
        const paintCells = cellsFromKeys()
          .filter((c) => !keepCellKeys.has(planCellKey(c.row, c.col)))
          .map((c) => ({ ...c, kind: 'empty' as const }));
        const paintDeleteOps: PlanOperation[] = entityCount > 0 && paintCells.length > 0
          ? [{ op: 'paintCells', cells: paintCells }]
          : [];
        const deleteOps = [...structuralDeleteOps, ...paintDeleteOps];

        if (bedCount > 0) {
          Modal.confirm({
            title: i18n.t('planning:planningActions.deleteTitle'),
            icon: <ExclamationCircleOutlined />,
            content: (
              <div>
                <p>
                  {bedCount > 0 && `${i18n.t('planning:planningActions.beds', { count: bedCount })} `}
                  {planningSelection.pathCellCount > 0 && `${i18n.t('planning:planningActions.paths', { count: planningSelection.pathCellCount })} `}
                  {planningSelection.obstacleCellCount > 0 && `${i18n.t('planning:planningActions.obstacles', { count: planningSelection.obstacleCellCount })} `}
                  {planningSelection.structuralObjectIds.length > 0 && `${i18n.t('planning:planningActions.objects', { count: planningSelection.structuralObjectIds.length })} `}
                </p>
                <p style={{ marginTop: 8, color: '#666' }}>{i18n.t('planning:planningActions.activePlantingsWarning')}</p>
              </div>
            ),
            okText: i18n.t('common:actions.delete'),
            okType: 'danger',
            cancelText: i18n.t('common:actions.cancel'),
            onOk: async () => {
              await onPatch(deleteOps);
              clearPlanningSelection();
            },
          });
        } else if (deleteOps.length > 0) {
          onPatch(deleteOps);
          clearPlanningSelection();
        }
        break;
      }
      default:
        break;
    }
  }, [planningSelection, cells, structuralObjects, onBedSelection, clearPlanningSelection, onPatch]);
};
